import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { COPYRIGHT_LINE } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OtpAuthPanel } from "@/components/OtpAuthPanel";
import { useForceLight } from "@/context/ThemeContext";

export default function AdminLogin() {
  useForceLight();
  const { login, applyUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [view, setView] = useState("password");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const data = await login(form.email, form.password);
      if (data?.requires_2fa) setChallenge(data.challenge);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge, code });
      await applyUser(data);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/"><Logo dark /></Link>
        </div>

        <div className="rounded-lg bg-white p-8 border border-[#E5E7EB]" data-testid="admin-login-card">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="h-5 w-5 text-[#002FA7]" />
            <span className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Administrator access</span>
          </div>

          {view === "forgot" ? (
            <OtpAuthPanel mode="reset" onBack={() => setView("password")} />
          ) : !challenge ? (
            <>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-[#111827]">Admin sign in</h1>
              <p className="text-sm text-[#6B7280] mt-1 mb-6">
                Restricted to laboratory administrators. Two-factor authentication is mandatory.
              </p>
              <form onSubmit={submit} className="space-y-4" data-testid="admin-auth-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Admin email</Label>
                  <Input type="email" value={form.email} onChange={set("email")} required
                    className="mt-1.5" data-testid="admin-email-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Password</Label>
                  <Input type="password" value={form.password} onChange={set("password")} required
                    className="mt-1.5" data-testid="admin-password-input" />
                </div>
                {error && <p className="text-sm text-[#C92A2A]" data-testid="admin-auth-error">{error}</p>}
                <Button type="submit" disabled={busy} data-testid="admin-submit-btn"
                  className="w-full bg-[#002FA7] hover:bg-[#00237C] text-white">
                  {busy ? "Please wait…" : "Sign in as admin"}
                </Button>
              </form>
              <div className="mt-5 flex items-center justify-between text-sm">
                <button type="button" onClick={() => setView("forgot")} data-testid="admin-forgot-btn"
                  className="text-[#002FA7] hover:underline">Forgot password?</button>
                <Link to="/login" className="text-[#6B7280] hover:underline" data-testid="admin-staff-login-link">
                  Staff sign in
                </Link>
              </div>
            </>
          ) : (
            <form onSubmit={verify} className="space-y-4" data-testid="admin-twofa-form">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-[#111827]">Two-factor check</h1>
              <p className="text-sm text-[#6B7280]">Enter the 6-digit code from your authenticator app.</p>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" autoFocus required
                data-testid="admin-twofa-code" />
              {error && <p className="text-sm text-[#C92A2A]" data-testid="admin-auth-error">{error}</p>}
              <Button type="submit" disabled={busy} data-testid="admin-twofa-verify-btn"
                className="w-full bg-[#002FA7] hover:bg-[#00237C] text-white">
                {busy ? "Verifying…" : "Verify and sign in"}
              </Button>
              <button type="button" onClick={() => { setChallenge(null); setCode(""); setError(""); }}
                data-testid="admin-twofa-back-btn" className="text-sm text-[#002FA7] hover:underline">
                Back to sign in
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs uppercase tracking-[0.18em] text-slate-500">
          LabTrack&trade; by PathLete&reg;
        </p>
        <div className="mt-2 text-center text-xs text-slate-500" data-testid="app-footer">
          <p>Proudly developed in India by PathLete Technologies</p>
          <p className="mt-1" data-testid="app-copyright">{COPYRIGHT_LINE}</p>
        </div>
      </div>
    </div>
  );
}
